import type { AnyValue } from "@/lib/rowTypes";
import { useCallback, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getModuleRetakeConfig } from "@/lib/retake/moduleRetakeConfig";
import { recordLearningEvent } from "@/lib/learning-engine/eventLogger";
import { getErrorMessage } from "@/lib/errors";

export interface ModuleRetakeResult {
  reversed_coins: number;
  reversed_xp: number;
  reversed_lesson_count: number;
}

/**
 * Confirmed module retake. Scoped strictly to the target module:
 *  - reverses money and XP granted by that module (compensating ledger entries)
 *  - revokes matching module completion achievements
 *  - deletes the module_completions row for this user+module
 *  - clears in-progress lesson_progress rows for lessons that belong to the
 *    module (empty for standalone beta modules)
 *
 * Idempotent server-side via `module_retake_events`; two rapid confirmations
 * will not double-reverse.
 */
export function useModuleRetake() {
  const [pending, setPending] = useState(false);

  const reverseAndReset = useCallback(async (moduleKey: string): Promise<ModuleRetakeResult | null> => {
    const cfg = getModuleRetakeConfig(moduleKey);
    if (!cfg) {
      toast.error("Retake not available for this module");
      return null;
    }
    setPending(true);
    // Adaptive Learning Engine: announce that the learner intends to retake
    // this module BEFORE the reversal RPC. Frontend-only signal; never
    // grants XP, coins, or unlocks — the server RPC remains the source of
    // truth for reward reversal.
    void recordLearningEvent({
      eventType: "module_retake_started",
      moduleKey,
      metadata: { source: "useModuleRetake" },
    });
    try {
      const { data, error } = await supabase.rpc("reverse_module_rewards", {
        p_module_key: moduleKey,
        p_lesson_ids: cfg.lessonIds,
        p_achievement_name_patterns: cfg.achievementNamePatterns,
      });
      if (error) throw error;
      const result = data as AnyValue as ModuleRetakeResult;
      // Log the confirmed reversal so the engine can attribute reward
      // impact and admin rollup can see retake volume. This event never
      // issues rewards or unlocks anything.
      void recordLearningEvent({
        eventType: "module_reward_reversed",
        moduleKey,
        metadata: {
          reversed_coins: result?.reversed_coins ?? 0,
          reversed_xp: result?.reversed_xp ?? 0,
          reversed_lesson_count: result?.reversed_lesson_count ?? 0,
        },
      });
      toast.success(`${cfg.displayName} reset`, {
        description:
          result.reversed_coins > 0 || result.reversed_xp > 0
            ? `Removed $${result.reversed_coins} and ${result.reversed_xp} XP earned from this module.`
            : "Module progress cleared.",
      });
      return result;
    } catch (err: unknown) {
      toast.error("Couldn't reset module", { description: getErrorMessage(err) ?? "Please try again." });
      return null;
    } finally {
      setPending(false);
    }
  }, []);

  return { reverseAndReset, pending };
}